import { Navigate } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import store from '../utils/store';
import api from '../utils/api';
import { verifyToken } from '../utils/utils';

import ErrorAlert from './ErrorAlert';
import LoadingPage from '../pages/LoadingPage';

export default function PrivRoute({ component: Component, ...rest }) {
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshed, setIsRefreshed] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const mountedRef = useRef(true);
  const tokenCheckTimeoutRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;

    const checkToken = async () => {
      try {
        const accessToken = store.getItem('accessToken');
        if (!accessToken) {
          throw new Error('No access token found');
        }

        await verifyToken();
        if (mountedRef.current) {
          setIsLoading(false);
          setErrorMsg('');

          // Schedule next token check in 14 minutes (before 15 min expiry)
          tokenCheckTimeoutRef.current = setTimeout(() => {
            setIsRefreshed(prev => !prev);
          }, 14 * 60 * 1000);
        }
      } catch (e) {
        if (mountedRef.current) {
          store.clear();
          window.location.href = '/login';
        }
      }
    };

    checkToken();

    return () => {
      mountedRef.current = false;
      if (tokenCheckTimeoutRef.current) {
        clearTimeout(tokenCheckTimeoutRef.current);
      }
    };
  }, [isRefreshed]);

  // If no token exists, redirect to login
  if (!store.getItem('accessToken')) {
    return <Navigate to="/login" state={{ from: rest.location }} replace />;
  }

  // Show loading state
  if (isLoading) {
    return <LoadingPage />;
  }

  // Show error state
  if (errorMsg) {
    return <ErrorAlert msg={errorMsg} />;
  }

  // Render protected component
  return <Component {...rest} />;
}
